import { useState } from 'react';

/**
 * SetupSteps — collapsible accordion for step-by-step setup guides.
 *
 * Each step has a number, title, and content (array of blocks rendered
 * as paragraphs and code blocks). Steps expand/collapse on click with
 * a smooth animation. A progress bar tracks completed steps.
 *
 * Props:
 *   steps: [{ title: "...", content: [{ type: "p"|"code"|"ul", ... }] }]
 */

function renderStepInline(text) {
  if (!text) return null;
  var parts = [];
  var regex = /\*\*(.+?)\*\*/g;
  var last = 0;
  var match;
  var idx = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    parts.push(<strong key={'b' + idx}>{match[1]}</strong>);
    last = regex.lastIndex;
    idx++;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts.length === 1 && typeof parts[0] === 'string' ? parts[0] : parts;
}

function renderStepBlock(block, i) {
  if (block.type === 'p') return <p key={i}>{renderStepInline(block.text)}</p>;
  if (block.type === 'code') {
    return (
      <div key={i} className="guide-code-editor">
        <div className="guide-code-titlebar">
          <div className="guide-code-dots">
            <span className="guide-code-dot guide-code-dot--red" />
            <span className="guide-code-dot guide-code-dot--yellow" />
            <span className="guide-code-dot guide-code-dot--green" />
          </div>
          <button
            className="guide-code-copy"
            type="button"
            onClick={function (e) {
              e.stopPropagation();
              navigator.clipboard.writeText(block.code).then(function () {
                var btn = e.currentTarget;
                if (btn) { btn.textContent = 'Copied!'; setTimeout(function () { btn.textContent = 'Copy'; }, 1500); }
              });
            }}
          >Copy</button>
        </div>
        <pre className="guide-code"><code>{block.code}</code></pre>
      </div>
    );
  }
  if (block.type === 'ul') {
    return (
      <ul key={i}>
        {block.items.map(function (item, li) {
          return <li key={li}>{renderStepInline(item)}</li>;
        })}
      </ul>
    );
  }
  return null;
}

function SetupSteps({ steps }) {
  var [openIndex, setOpenIndex] = useState(0);
  var [completed, setCompleted] = useState({});

  function toggle(idx) {
    setOpenIndex(openIndex === idx ? -1 : idx);
  }

  function markDone(idx, e) {
    e.stopPropagation();
    var next = {};
    for (var k in completed) next[k] = completed[k];
    next[idx] = !completed[idx];
    setCompleted(next);
    // Auto-open next step
    if (!completed[idx] && idx < steps.length - 1) {
      setOpenIndex(idx + 1);
    }
  }

  var doneCount = 0;
  for (var k in completed) { if (completed[k]) doneCount++; }
  var progress = steps.length > 0 ? (doneCount / steps.length) * 100 : 0;

  return (
    <div className="setup-steps">
      {/* Progress bar */}
      <div className="setup-steps-progress">
        <div className="setup-steps-progress-bar" style={{ width: progress + '%' }} />
        <span className="setup-steps-progress-label">
          {doneCount} of {steps.length} steps complete
        </span>
      </div>

      {steps.map(function (step, idx) {
        var isOpen = openIndex === idx;
        var isDone = !!completed[idx];

        return (
          <div
            key={idx}
            className={
              'setup-step' +
              (isOpen ? ' setup-step--open' : '') +
              (isDone ? ' setup-step--done' : '')
            }
          >
            <button
              className="setup-step-header"
              onClick={function () { toggle(idx); }}
              type="button"
              aria-expanded={isOpen}
            >
              <span className="setup-step-number">{isDone ? '\u2713' : idx + 1}</span>
              <span className="setup-step-title">{step.title}</span>
              <span className={'setup-step-chevron' + (isOpen ? ' setup-step-chevron--open' : '')}>
                {'\u25BE'}
              </span>
            </button>

            <div
              className="setup-step-body"
              style={{
                maxHeight: isOpen ? '2000px' : '0',
                opacity: isOpen ? 1 : 0,
              }}
            >
              <div className="setup-step-content">
                {step.content.map(renderStepBlock)}

                <button
                  className={'setup-step-done-btn' + (isDone ? ' setup-step-done-btn--active' : '')}
                  onClick={function (e) { markDone(idx, e); }}
                  type="button"
                >
                  {isDone ? '\u2713 Completed' : 'Mark as done'}
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default SetupSteps;
